/**
 * Schedule service for fetching truck schedule data
 * Now supports environment-specific API calls with fallback to mock data
 */

import { ScheduleEntry } from '../types';
import { createApiClient } from '../utils/apiClient';
import { envLog } from '../config/environment';
import { formatTime12Hour } from '../utils';

export interface TruckScheduleGetRequest {
  truckId: string;
  vendorId: string;
  clientType: 'WEBAPP';
  fromTimeStamp: string;
  toTimeStamp: string;
  getOrdersCount: boolean;
}

export interface TruckScheduleGetResponse {
  result?: ScheduleEntry[];
  scheduleData?: ScheduleEntry[];
  [key: string]: any;
}

/**
 * Generate timestamp for start of today (midnight)
 */
const getTodayMidnight = (): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.toISOString();
};

/**
 * Generate timestamp for end of day, 7 days from the given start date
 */
const getSevenDaysEndOfDay = (fromTimeStamp: string): string => {
  // Parse the start date and add exactly 6 days (since we want 7 days total including start day)
  const startDate = new Date(fromTimeStamp);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);
  return endDate.toISOString();
};

/**
 * Fetch schedule data for a specific truck using the truckScheduleGet endpoint
 * @param truckId - The truck identifier
 * @param vendorId - The vendor identifier
 * @param environment - The environment to use for API calls
 * @returns Promise resolving to array of schedule entries
 */
export const fetchTruckSchedule = async (
  truckId: string,
  vendorId: string,
  environment: string = 'app'
): Promise<ScheduleEntry[]> => {
  const apiClient = createApiClient(environment);
  
  try {
    envLog(environment, 'Fetching truck schedule', { truckId, vendorId });
    
    // Special test case: if truckId is 'test-error', simulate an error
    if (truckId === 'test-error') {
      throw new Error('Simulated API error for testing toast functionality');
    }
    
    const fromTimeStamp = getTodayMidnight();
    
    const requestBody: TruckScheduleGetRequest = {
      truckId,
      vendorId,
      clientType: 'WEBAPP',
      fromTimeStamp,
      toTimeStamp: getSevenDaysEndOfDay(fromTimeStamp),
      getOrdersCount: false
    };

    envLog(environment, 'Request body for truckScheduleGet', requestBody);
    
    // Try to fetch real data from API
    const response = await apiClient.post<TruckScheduleGetResponse>(
      '/parse/functions/truckScheduleGet',
      requestBody
    );
    
    envLog(environment, 'Truck schedule fetched successfully', response.data);
    
    // Parse Parse Server response format - the actual data might be in response.data.result
    const rawData = response.data.result || response.data.scheduleData || [];
    
    envLog(environment, 'Raw schedule data from API', { rawData, count: rawData.length });
    
    // Log the first item to understand the structure
    if (rawData.length > 0) {
      envLog(environment, 'First schedule item structure', rawData[0]);
      envLog(environment, 'All property names in first item', Object.keys(rawData[0]));
      
      // Check if there are nested objects that might contain time data
      Object.keys(rawData[0]).forEach(key => {
        const value = (rawData[0] as any)[key];
        if (typeof value === 'object' && value !== null) {
          envLog(environment, `Nested object in ${key}`, value);
        }
      });
    }
    
    // Transform the API response to match our ScheduleEntry interface
    const transformedData: ScheduleEntry[] = rawData.map((item: any, index: number) => {
      envLog(environment, `Transforming item ${index}`, item);

      const startDate = new Date(item.startDateTime.iso);
      const date = startDate.toISOString().split('T')[0];

      let dayOfWeek;
      try {
        const dateObj = new Date(startDate);
        dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
      } catch (e) {
        dayOfWeek = 'Unknown';
      }
      
      // Extract times from DateTime fields
      let openTime = null;
      let closeTime = null;
      let deliveryStartTime = null;
      let deliveryEndTime = null;
      
      if (item.startDateTime) {
        try {
          envLog(environment, `Parsing startDateTime: ${item.startDateTime}`);
          const startDate = new Date(item.startDateTime.iso);
          if (!isNaN(startDate.getTime())) {
            const hours = startDate.getHours().toString().padStart(2, '0');
            const minutes = startDate.getMinutes().toString().padStart(2, '0');
            openTime = `${hours}:${minutes}`;
            envLog(environment, `Extracted openTime: ${openTime}`);
          }
        } catch (e) {
          console.warn('Could not parse startDateTime:', item.startDateTime, e);
        }
      }
      
      if (item.endDateTime) {
        try {
          envLog(environment, `Parsing endDateTime: ${item.endDateTime}`);
          const endDate = new Date(item.endDateTime.iso);
          if (!isNaN(endDate.getTime())) {
            const hours = endDate.getHours().toString().padStart(2, '0');
            const minutes = endDate.getMinutes().toString().padStart(2, '0');
            closeTime = `${hours}:${minutes}`;
            envLog(environment, `Extracted closeTime: ${closeTime}`);
          }
        } catch (e) {
          console.warn('Could not parse endDateTime:', item.endDateTime, e);
        }
      }

      // Extract delivery times if they exist and are different from regular hours
      if (item.deliveryStart && item.deliveryStart.iso) {
        try {
          envLog(environment, `Parsing deliveryStart: ${item.deliveryStart}`);
          const deliveryStartDate = new Date(item.deliveryStart.iso);
          if (!isNaN(deliveryStartDate.getTime())) {
            const hours = deliveryStartDate.getHours().toString().padStart(2, '0');
            const minutes = deliveryStartDate.getMinutes().toString().padStart(2, '0');
            deliveryStartTime = `${hours}:${minutes}`;
            envLog(environment, `Extracted deliveryStartTime: ${deliveryStartTime}`);
          }
        } catch (e) {
          console.warn('Could not parse deliveryStart:', item.deliveryStart, e);
        }
      }

      if (item.deliveryEnd && item.deliveryEnd.iso) {
        try {
          envLog(environment, `Parsing deliveryEnd: ${item.deliveryEnd}`);
          const deliveryEndDate = new Date(item.deliveryEnd.iso);
          if (!isNaN(deliveryEndDate.getTime())) {
            const hours = deliveryEndDate.getHours().toString().padStart(2, '0');
            const minutes = deliveryEndDate.getMinutes().toString().padStart(2, '0');
            deliveryEndTime = `${hours}:${minutes}`;
            envLog(environment, `Extracted deliveryEndTime: ${deliveryEndTime}`);
          }
        } catch (e) {
          console.warn('Could not parse deliveryEnd:', item.deliveryEnd, e);
        }
      }

      // Generate delivery note if delivery times differ from regular hours
      let deliveryNote = null;
      const hasDeliveryTimes = deliveryStartTime && deliveryEndTime;
      const deliveryDiffersFromRegular = hasDeliveryTimes && 
        (deliveryStartTime !== openTime || deliveryEndTime !== closeTime);

      if (deliveryDiffersFromRegular) {
        const formattedDeliveryStart = formatTime12Hour(deliveryStartTime);
        const formattedDeliveryEnd = formatTime12Hour(deliveryEndTime);
        
        if (formattedDeliveryStart && formattedDeliveryEnd) {
          deliveryNote = `Delivery starts at ${formattedDeliveryStart} and ends at ${formattedDeliveryEnd}`;
          envLog(environment, `Generated delivery note: ${deliveryNote}`);
        }
      }

      const transformedItem = {
        date: date || 'unknown',
        dayOfWeek: dayOfWeek || 'Unknown',
        openTime,
        closeTime,
        isOpen: !!(openTime && closeTime), // Simple: has times = open
        isPrivate: !!item.isPrivate, // Extract isPrivate flag
        notes: deliveryNote || undefined
      };
      
      envLog(environment, `Transformed item ${index}`, transformedItem);
      return transformedItem;
    });
    
    envLog(environment, 'Transformed schedule data', { transformedData, count: transformedData.length });
    
    // Generate complete 7-day schedule based on the date range requested
    const completeSchedule = generateCompleteSchedule(transformedData);
    
    envLog(environment, 'Complete 7-day schedule', { completeSchedule, count: completeSchedule.length });
    
    return completeSchedule;
    
  } catch (error) {
    envLog(environment, 'Failed to fetch schedule data', error);
    
    // Throw error instead of falling back to mock data so UI can handle retry
    throw new Error('Failed to retrieve schedule. Please try again.');
  }
};

/**
 * Generate a complete 7-day schedule based on API response
 * Days with schedule entries = OPEN, days without = CLOSED
 * Now supports multiple schedule entries per day
 */
const generateCompleteSchedule = (apiScheduleData: ScheduleEntry[]): ScheduleEntry[] => {
  const completeSchedule: ScheduleEntry[] = [];
  const today = new Date();
  
  // Create a map of dates to arrays of schedule entries from API
  const scheduleMap = new Map<string, ScheduleEntry[]>();
  apiScheduleData.forEach(entry => {
    if (entry.date) {
      if (!scheduleMap.has(entry.date)) {
        scheduleMap.set(entry.date, []);
      }
      scheduleMap.get(entry.date)!.push(entry);
    }
  });
  
  // Generate 7 days starting from today
  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const dateString = date.toISOString().split('T')[0];
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Check if API returned schedule data for this date
    const apiEntries = scheduleMap.get(dateString);
    
    if (apiEntries && apiEntries.length > 0) {
      // API has data for this date - add all entries for this day
      apiEntries.forEach(apiEntry => {
        completeSchedule.push({
          date: dateString,
          dayOfWeek,
          openTime: apiEntry.openTime,
          closeTime: apiEntry.closeTime,
          isOpen: !!(apiEntry.openTime && apiEntry.closeTime), // Has valid times = open
          isPrivate: apiEntry.isPrivate, // Preserve isPrivate flag
          notes: apiEntry.notes
        });
      });
    } else {
      // No API data for this date = CLOSED
      completeSchedule.push({
        date: dateString,
        dayOfWeek,
        openTime: null,
        closeTime: null,
        isOpen: false, // No schedule data = closed
        notes: undefined
      });
    }
  }
  
  return completeSchedule;
};

/**
 * Legacy function - now redirects to fetchTruckSchedule
 * @deprecated Use fetchTruckSchedule instead
 */
export const fetchScheduleData = async (
  truckId: string,
  vendorId: string,
  environment: string = 'app'
): Promise<ScheduleEntry[]> => {
  // Legacy support - just call the new function
  return fetchTruckSchedule(truckId, vendorId, environment);
};
